import React, { ButtonHTMLAttributes } from "react";
import { motion, MotionProps } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Import the button variants from shadcn
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-60 disabled:grayscale [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        "outline-primary":
          "border bg-transparent dark:border-mforange border-mforange text-mforange shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50",
        "outline-red":
          "border bg-transparent dark:border-red-500 border-red-500 text-red-500 shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "rounded px-2",
        sm: "h-8 rounded gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);


// Combine ButtonHTMLAttributes, MotionProps, and VariantProps
export interface FramerButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof MotionProps>,
    MotionProps,
    VariantProps<typeof buttonVariants> {}

/**
 * `FramerButton` is a custom React button component that combines the
 * shadcn/ui button styling and variants with `framer-motion` animation props.
 * It allows you to add motion effects such as hover and tap animations while
 * maintaining the consistent design system from shadcn/ui.
 *
 * The button includes all shadcn button variants (default, destructive, outline,
 * secondary, ghost, link) and sizes (default, xs, sm, lg, icon) with smooth
 * motion animations.
 *
 * @property {React.ReactNode} [children] - The content inside the button.
 * @property {"default" | "destructive" | "outline" | "secondary" | "ghost" | "link"} [variant] - The button variant style.
 * @property {"default" | "xs" | "sm" | "lg" | "icon"} [size] - The button size.
 * @property {string} [className] - Additional CSS classes to apply.
 * @property {ButtonHTMLAttributes<HTMLButtonElement>} ...props - Any additional button HTML attributes.
 * @property {MotionProps} ...props - Props from `framer-motion` for animation controls.
 *
 * @param {FramerButtonProps} props - The props for the FramerButton.
 * @returns {JSX.Element} The animated button component with shadcn styling.
 *
 * @example
 * // Basic usage with default variant
 * <FramerButton onClick={() => console.log('Clicked!')}>
 *   Click Me
 * </FramerButton>
 *
 * @example
 * // With variant and size
 * <FramerButton
 *   variant="outline"
 *   size="lg"
 *   onClick={() => console.log('Clicked!')}
 * >
 *   Large Outline Button
 * </FramerButton>
 *
 * @example
 * // With custom motion props
 * <FramerButton
 *   variant="destructive"
 *   onClick={() => console.log('Clicked!')}
 *   whileHover={{ scale: 1.05 }}
 *   whileTap={{ scale: 0.95 }}
 * >
 *   Custom Animation
 * </FramerButton>
 */
const FramerButton = React.forwardRef<HTMLButtonElement, FramerButtonProps>(
  ({ className, variant, size, children, disabled, ...props }, ref) => {
    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, className }))}
        whileHover={disabled ? undefined : { opacity: 0.7 }}
        whileTap={disabled ? undefined : { scale: 0.95, transition: { duration: 0.1 } }}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {children}
      </motion.button>
    );
  }
);

FramerButton.displayName = "FramerButton";

export default FramerButton;
